'use client'
import { FC, memo, useCallback, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import { motion, useInView } from 'motion/react'
import { useModal, useTranslator } from '@/hooks'
import { fadeFromBottomVariants, listItemVariants, listVariants, sortBy } from '@/utils'
import { designGrid, mq, presets, rgbAlpha } from '@/styles'
import type { TypeCmsContentTwoColumns } from '@/schema'
import { MODALS } from '@/context'
import { getMediaPreview } from './ContentHeader'

type Props = {
  data: TypeCmsContentTwoColumns['data']
}

type PropsArticle = {
  data: TypeCmsContentTwoColumns['data']['columns'][0]
}

const Item:FC<PropsArticle> = memo(({ data }) => {
  const { media, title, subtitle, price, markdownContent } = data
  const { t } = useTranslator()
  const { updatePayload } = useModal(MODALS.GALLERY)
  const [playVideo, setPlayVideo] = useState(true)

  const onClickSeePhotos = useCallback(() => {
    updatePayload({
      open: true,
      close: false,
      payload: { data: { media, title: `${title} - ${subtitle}` } }
    })
  }, [media, title, subtitle, updatePayload])

  return (
    <Article variants={listItemVariants}>
      <div className='media-wrapper'>
        {getMediaPreview(data?.media?.[0], playVideo, setPlayVideo)}
      </div>
      <h3>{title}</h3>
      <div className='text'>
        <p>{subtitle}</p>
        {markdownContent && <div className='md' dangerouslySetInnerHTML={{ __html: markdownContent }} />}
      </div>
      <div className='text-bottom'>
        <p className='price'>{price}</p>
        {media?.length > 1 &&
          <button className='btn' onClick={onClickSeePhotos}>
            <svg xmlns='http://www.w3.org/2000/svg' width='14' height='13' viewBox='0 0 14 13' fill='none'>
              <path d='M9.72725 9.22727H13V0.5H4.27271V3.77273' stroke='black' strokeLinecap='round' strokeLinejoin='round' />
              <path d='M9.72727 3.77271H1V12.5H9.72727V3.77271Z' stroke='black' strokeLinecap='round' strokeLinejoin='round' />
            </svg>
            <span>{t('project.see_photos')}</span>
          </button>
        }
      </div>
    </Article>
  )
})

const getItems = (data: Props['data']['columns']) => data.map((item, idx) => (
  <Item key={idx} data={item} />
))

const headingId = (str = '0') => `s-two-cls-${str}`
export const ContentTwoCols:FC<Props> = memo(({ data }) => {
  const { title, columns } = data
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true })

  // console.log('🆒 🆒 ContentTwoCols data', data)

  const Items = useMemo(() => getItems(sortBy({ arr: columns, keyname: 'id' }).slice(-2) ?? []), [columns])

  return (
    <Section ref={ref} variants={listVariants} initial='initial' animate={isInView ? 'animate' : 'initial'}>
      <motion.h2 id={headingId()} variants={fadeFromBottomVariants}>
        {title}
      </motion.h2>
      {Items}
    </Section>
  )
})

const Section = styled(motion.section)`
  ${designGrid({})}
  margin-bottom: 50px;
  padding-top: 50px;
  width: 100vw;
  width: 100dvw;

  ${mq.greaterThan('tablet')} {
    margin-bottom: 70px;
    padding-top: 70px;
  }

  h2 {
    ${presets.font40_60()}
    grid-column: 1 / span 6;
    grid-row: 1 / span 1;
    margin-bottom: 30px;
    text-align: left;
    width: 100%;

    ${mq.greaterThan('tablet')} {
      grid-column: 1 / span 12;
      margin-bottom: 50px;
      text-align: center;
    }
  }
`

const Article = styled(motion.article)<{ $playVideo?: boolean }>`
  background-color: ${rgbAlpha('--color-black', .2)};
  border-radius: 20px;
  color: var(--color-white);
  display: flex;
  flex-wrap: wrap;
  grid-row: auto / span 1;
  grid-column: auto / span 6;
  margin-bottom: 10px;
  min-height: 400px;
  overflow: hidden;
  padding: 30px 20px;
  position: relative;
  width: 100%;

  ${mq.greaterThan('tablet')} {
    flex-direction: column;
    margin-bottom: 0;
    min-height: 650px;
    padding: 40px 30px;
  }

  h3,
  p,
  .text,
  .text-bottom {
    z-index: 2;
  }

  > div {
    width: 100%;

    &.text {
      align-self: flex-end;
      display: flex;
      flex-direction: column;

      ${mq.greaterThan('tablet')} {
        flex-direction: row;
        justify-content: space-between;
      }

      p,
      .md {
        margin-bottom: 20px;

        ${mq.greaterThan('tablet')} {
          margin-bottom: 30px;
        }
      }

      p {
        ${presets.font30_35()}

        ${mq.greaterThan('tablet')} {
          width: 63.5%;
        }
      }
    }

    &.text-bottom {
      align-items: center;
      align-self: flex-end;
      border-top: 1px solid ${rgbAlpha('--color-white', .2)};
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      padding-top: 20px;
      width: 100%;

      ${mq.greaterThan('tablet')} {
        padding-top: 30px;
      }
    }
  }

  h3 {
    ${presets.font15_height_16_5()}
    margin-bottom: auto;
    text-align: left;
    text-transform: uppercase;
    width: 100%;
  }

  .md {

  }

  .price {
    ${presets.font15()}
  }

  .btn {
    align-items: center;
    background-color: var(--color-gold);
    border-radius: 100px;
    border: 2px solid var(--color-gold);
    color: var(--color-black);
    display: flex;
    gap: 10px;
    height: auto;
    height: fit-content;
    justify-content: center;
    padding: 15px 30px;
    transition: border-color 250ms ease-in-out;

    &:hover {
      border-color: var(--color-black);
    }

    svg {
      aspect-ratio: 1/1;
      height: 12px;
      width: 12px;
    }

    span {
      ${presets.font15_seephotos()}
    }
  }

  .media-wrapper {
    border-radius: 20px;
    bottom: 0;
    height: 100%;
    left: 0;
    position: absolute;
    right: 0;
    top: 0;
    width: 100%;

    img,
    video {
      height: 100%;
      width: 100%;
      object-fit: cover;
    }

    img {
      &.cover {
        opacity: ${({ $playVideo }) => $playVideo ? 0 : 1 };
        position: absolute;
        height: 100%;
        object-fit: cover;
        transition: 300ms opacity linear;
        width: 100%;
      }
    }
  }
`
